import { Deserializer } from 'jsonapi-serializer';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { Reader, ReaderWrapper } from '../models/Reader';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { APIConnector } from './api/APIConnector';
import { getBiometricDeviceByAssignedDeviceImprox } from './biometricService';
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { getDeviceLicenceStatus } from "./utilities/StatusBuilder";
import { INTERNAL_SERVER_ERROR, MAXIMUM_STATUS_CODE, MINIMUM_STATUS_CODE, NO_CONTENT } from '../constants/response-status-codes';
import { DEVICE_CATEGORIES } from "../constants/device-constants";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { Translator } from '../contexts/translator';
import { TranslationKeys } from '../contexts/TranslationKeys';
import { isSuccessStatus } from '../utilities/requestResponseValidator';

const apiConnector = new APIConnector();

const filterByNullAndLocationId = (disconnectedOnly, locationId) => {
    return disconnectedOnly
        ? `location=isnull=true${locationId
            ? `,location.id==${locationId}`
            : ''}`
        : ''
};

export const getReaderNames = async (listQuery?: ListQuery, disconnectedOnly?: boolean, locationId?: number): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filterDoor = filterByNullAndLocationId(disconnectedOnly, locationId);
        const filterCategory = `unitType.utCategory==${DEVICE_CATEGORIES.READER}`;
        let filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        filter = filter ? `${filter};${filterDoor};${filterCategory}`
            : `&filter=${filterDoor};${filterCategory}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filter);
        const includes = `include=deviceImprox,location.zoneByZoneId`;
        const fieldsTerminal = `&fields[terminal]=devName`;
        const url = `terminal?${includes}${fieldsTerminal}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const readers: Name[] = items.map((reader): Name => {
                    return {
                        value: reader['id'],
                        label: reader['dev-name']
                    };
                });

                return resolve({ rows: readers, pagination: newPagination });
            })

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReaders = async (translator: Translator, listParameters?: ListQuery): Promise<ReaderWrapper> => {
    try {
        const filterCategory = `unitType.utCategory==${DEVICE_CATEGORIES.READER}`;
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter ? `${filter};${filterCategory}` : `&filter=${filterCategory}`;
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=unitType,site,profile,controller,location";
        const terminalFields = "fields[terminal]=devName,devFixedAddr,musteringPoint,devIpAddress,devEnabled,devCommsStatus,interpretedLicense,deviceImprox,location,controller,unitType,site,profile,devSerial,devReaderSerial,hasPendingLicense,liftName";
        const locationFields = "fields[location]=locName";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const url = `terminal?${includes}&${terminalFields}&${locationFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const readers: Reader[] = data.map((reader): Reader => {
                    const door = reader['location'];
                    const devSerial = reader['dev-serial'];
                    const devReaderSerial = reader['dev-reader-serial']; // Used when devSerial is actually storing the module serial.
                    return {
                        id: reader['id'],
                        name: reader['dev-name'],
                        fixedAddress: reader['dev-fixed-addr'],
                        ipAddress: reader['dev-ip-address'] ? reader['dev-ip-address'] : '-',
                        site: reader['site']['site-name'],
                        preset: reader['profile']['trn-name'],
                        presetId: reader['profile']['id'],
                        type: reader['unit-type']['trn-name'],
                        controller: reader['controller']['dev-name'],
                        controllerAddress: reader['controller']['dev-fixed-addr'],
                        door: door ? door['loc-name'] : "-",
                        enabled: reader['dev-enabled'],
                        devCommsStatus: reader['dev-comms-status'],
                        status: getDeviceLicenceStatus(true, reader['dev-enabled'], reader['dev-comms-status']),
                        musteringPoint: reader['mustering-point'] == 1 ? true : false,
                        musteringPointLabel: reader['mustering-point'] == 1 ? translator.byKey(TranslationKeys.yes) : translator.byKey(TranslationKeys.no),
                        devLicense: reader['interpreted-license'] ? reader['interpreted-license'] : reader['has-pending-license'] ? '' : '-', //If we have a pending license we will show an icon
                        devReaderSerial: devReaderSerial ? devReaderSerial : devSerial ? devSerial : '-',
                        pendingLicense: reader['has-pending-license'],
                        liftName: reader['lift-name']? reader['lift-name'] : '-'
                    };
                });

                return resolve({ rows: readers, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReadersById = async (readerId: string): Promise<Reader> => {
    try {
        const includes = "include=unitType,site,profile,controller,location,locationType,deviceImprox,modeType,threatLevel";
        const terminalFields = "fields[terminal]=devName,devDesc,devFixedAddr,devSla,devDirection,musteringPoint,devIpAddress,devFwVersion,devEnabled,devCommsStatus,totalActions,totalParameters,totalInputs,interpretedLicense,deviceImprox,location,locationType,controller,unitType,site,profile,modeType,threatLevel,devReaderSerial,liftName";
        const locationFields = "fields[location]=locName";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName,pSysnameType";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const locationTypeFields = "fields[locationType]=ltName";
        const modeTypeFields = "fields[modeType]=trnName";
        const threatLevelFields = "fields[threatLevel]=tlName";
        const url = `terminal/${readerId}?${includes}&${terminalFields}&${locationFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}&${locationTypeFields}&${modeTypeFields}&${threatLevelFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const biometricValues = await getBiometricDeviceByAssignedDeviceImprox(readerId);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const door = data['location'];
                const reader: Reader = {
                    id: data['id'],
                    name: data['dev-name'],
                    fixedAddress: data['dev-fixed-addr'],
                    ipAddress: data['dev-ip-address'] ? data['dev-ip-address'] : '-',
                    logicalAddress: data['dev-sla'],
                    description: data['dev-desc'] ?? '',
                    terminalId: data['id'],
                    totalActions: data['total-actions'],
                    totalParameters: data['total-parameters'],
                    totalInputs: data['total-inputs'],
                    positionKey: data['dev-direction'],
                    musteringPoint: data['mustering-point'] == 1 ? true : false,
                    position: !!data['dev-direction'] ? 'Exit' : 'Entry',
                    version: data['dev-fw-version'],
                    enabled: data['dev-enabled'],
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    presetId: data['profile']['id'],
                    preset: data['profile']['trn-name'],
                    profileSysname: data['profile']['p-sysname-type'],
                    type: data['unit-type']['trn-name'],
                    typeId: data['unit-type']['id'],
                    controller: data['controller']['dev-name'],
                    controllerAddress: data['controller']['dev-fixed-addr'],
                    door: door ? door['loc-name'] : door,
                    defaultMode: data['mode-type']['trn-name'],
                    defaultModeId: data['mode-type']['id'],
                    functionId: data['location-type']['id'],
                    function: data['location-type']['lt-name'],
                    threatLevelId: data['threat-level']['id'],
                    threatLevel: data['threat-level']['tl-name'],
                    biometricInput: biometricValues != null ? biometricValues.value : '',
                    biometricInputLabel: biometricValues != null ? biometricValues.label : '',
                    unlinkBiometricInput: biometricValues != null ? biometricValues.value : '',
                    devLicense: data['interpreted-license'] ? data['interpreted-license'] : '-',
                    devReaderSerial: data['dev-reader-serial'] ? data['dev-reader-serial'] : '-',
                    liftName: data['lift-name'] ? data['lift-name'] : '-'
                };

                return resolve(reader);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReaderNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filterCategory = `unitType.utCategory==${DEVICE_CATEGORIES.READER}`;
        let filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        filters = filters ? `${filters};${filterCategory}`
            : `&filter=${filterCategory}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const includes = `include=deviceImprox,location.zoneByZoneId,controller`;
        const fieldsTerminal = `&fields[terminal]=devName,deviceImprox,controller`;
        const fieldsController = `&fields[controller]=devName`;
        const url = `terminal?${includes}${fieldsTerminal}${fieldsController}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const readers: Name[] = items.map((reader): Name => {
                    return {
                        value: reader['id'],
                        label: reader['dev-name'],
                        optionalColumns: [{ key: 'controller', value: reader["controller"]["dev-name"] || '-' }],
                    };
                });

                return resolve({ rows: readers, pagination: newPagination });
            })

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeReader = async (reader, createReader = true): Promise<StoreEntityResult> => {
    try {
        const checkAssignedDevice = !reader.biometricInput && reader.unlinkBiometricInput ? true : false;
        const newRecord = {
            data: {
                type: 'terminal',
                id: reader.id,
                attributes: {
                    devDesc: reader.description,
                    devEnabled: !!reader.enabled,
                    devName: reader.name,
                    selectedProfileId: reader.selectedPresetId,
                    musteringPoint: !!reader.musteringPoint
                },
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: reader.presetId
                        }
                    },
                    threatLevel: {
                        data: {
                            type: "threatLevel",
                            id: reader.threatLevelId
                        }
                    },
                    linkBioAddress: {
                        data: reader.biometricInput ? {
                            type: "bioAddress",
                            id: reader.biometricInput
                        } : undefined
                    },
                    unLinkBioAddress: {
                        data: checkAssignedDevice ? {
                            type: "bioAddress",
                            id: reader.unlinkBiometricInput
                        } : undefined
                    },
                    locationType: {
                        data: {
                            type: 'locationType',
                            id: reader.functionId
                        },
                    },
                    modeType: {
                        data: {
                            type: 'modeType',
                            id: reader.defaultModeId
                        },
                    }
                }
            }
        }

        const url = `terminal${createReader ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await Promise
            .all([apiConnector.sendData(url, createReader, newRecord),])
            .then(data => {
                response = data[0];
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createReader) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newReader) => {
                        if (error) {
                            return reject({ status: response.status, id: newReader.id, success: false });
                        }
                        return resolve({ id: newReader.id, success: true });
                    })

                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: reader.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: reader.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: reader.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            return reject(processServerError(error));
        });
    }
}

export const assignPresetReaders = async (
    readerPresetId: string,
    readerPresetToIdsAdd: string[]
) => {
    try {
        const readerPresetToAdd = readerPresetToIdsAdd.map((readerId) => ({
            data: {
                type: "deviceImprox",
                id: readerId,
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: readerPresetId,
                        },
                    },
                },
            },
        }));

        const addedModules = readerPresetToAdd.map((reader) =>
            apiConnector.sendData(
                `deviceImprox/${reader.data.id}`,
                false,
                reader
            )
        );

        let response: Response;
        return new Promise((resolve, reject) => {

            Promise.all(addedModules)
                .then((data: Response[]) => {
                    response = data[0];
                    if (!response.ok) {
                        reject({ success: response.ok });
                    }
                    resolve({ success: response.ok });
                })
                .catch((error) => {
                    return reject(processServerError(error));
                });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

/**
 * Get JSON error from response if available. Handles cases where response is not JSON.
 * @param response 
 * @returns 
 */
async function extractJsonError(response: Response) {
    let json: any = null;

    try {
        json = await response.json();
    }
    catch (e) {
        console.log('Error parsing response as JSON:', e);
    }

    return json;
}

export const exportMobileReaderLicense = async (readerToLicense: string, unlockingFor: string, translate: Translator) => {
    try {
        const url = "";
        const headers = {
            type: "mobileReaderLicense",
            unlockAttributeId: unlockingFor,
            ids: readerToLicense,
        }

        return buildFileContent(url, headers, translate);
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const exportBundledCredentialRequest = async (translate: Translator) => {
    try {
        const url = "";
        const headers = {
            type: "mobileBundledCredentials"
        }

        return buildFileContent(url, headers, translate);
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

const buildFileContent = async (url, headers, translate): Promise<any> => {
    try {
        const response = await apiConnector.fetchData(url, true, headers, "BYTES");
        const json = await extractJsonError(response);

        if (json != null && json.errors) {
            return Promise.reject(getConstraintViolation(json.errors[0].detail));
        }

        if (!isSuccessStatus(response.status)) {
            return Promise.reject(translate.byKey(TranslationKeys.exportLicenseFailed));
        }

        return new Promise(async (resolve, reject) => {
            const hasFile = (json['attributes']['file'] != null);
            const exportLicense = {
                id: 0,
                contentType: json['attributes']['contentType'],
                processedCount: json['attributes']['processedCount'],
                fileType: !hasFile ? '' : json['attributes']['file']['fileType'],
                fileName: !hasFile ? '' : json['attributes']['file']['fileName'],
                fileContentBytes: !hasFile ? [] : json['attributes']['file']['fileContentBytes'],
                fileSize: !hasFile ? '' : json['attributes']['file']['fileSize'],
                unlockAttributeId: !hasFile ? '' : json['attributes']['unlockAttributeId'],
                type: json['type'],
            }

            return resolve(exportLicense);
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const importReaderLicense = async (fileContent: any): Promise<StoreEntityResult> => {
 try {
        const newRecord = {
            type: "mobileReaderLicense",
            attributes: {
                file: {
                    fileContentBytes: fileContent
                }
            }
        }

        let response;
        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        const json = await response.json();
        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (json && json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            if (response) {
                if (response.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(response.errors[0].detail) })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}